import { ChangeEvent, useId, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    CircularProgress,
    IconButton,
    Typography,
} from '@mui/material';
import useProfile from 'hooks/use-update-profile-query';
import { immediateToast } from 'utils/toast';

const PROFILE_AVATAR_URL =
    import.meta.env.VITE_APP_API_URL + 'user/me/avatar';
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];

const getExtension = (fileName: string) =>
    fileName.split('.').pop()?.toLowerCase() ?? '';

const getAuthHeaders = () => {
    const headers = new Headers({ Accept: 'application/json' });
    const token = localStorage.getItem('token');

    if (token) headers.set('Authorization', `Bearer ${token}`);

    return headers;
};

const getResponseErrorMessage = async (response: Response) => {
    const payload = await response.json().catch(() => null);
    const validationMessage = payload?.errors
        ? Object.values(payload.errors).flat().find(Boolean)
        : null;

    return (
        validationMessage ||
        payload?.message ||
        'بارگذاری تصویر پروفایل انجام نشد. لطفاً دوباره تلاش کنید.'
    );
};

const ProfileAvatarUploader = () => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { profile, update } = useProfile();

    const resetNativeInput = () => {
        if (inputRef.current) inputRef.current.value = '';
    };

    const showError = (message: string) => {
        immediateToast('error', { message, timeout: 4000 });
    };

    const openFilePicker = () => {
        if (!isLoading) inputRef.current?.click();
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !profile) return;

        const extension = getExtension(file.name);
        const validExtension = ALLOWED_EXTENSIONS.includes(extension);
        const validMime = !file.type || ALLOWED_MIME_TYPES.includes(file.type);

        if (!validExtension || !validMime) {
            resetNativeInput();
            showError('فرمت تصویر باید JPG، JPEG یا PNG باشد.');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            resetNativeInput();
            showError('حجم تصویر نباید بیشتر از ۲ مگابایت باشد.');
            return;
        }

        const body = new FormData();
        body.append('file', file);

        setIsLoading(true);

        try {
            const response = await fetch(PROFILE_AVATAR_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body,
            });

            if (!response.ok) {
                throw new Error(String(await getResponseErrorMessage(response)));
            }

            const payload = await response.json();
            const avatar = payload?.data;

            if (!avatar) {
                throw new Error('آدرس تصویر بارگذاری‌شده از سرور دریافت نشد.');
            }

            update({ ...profile, avatar });
            immediateToast('success', {
                message: 'تصویر پروفایل بروزرسانی شد',
                timeout: 2000,
            });
        } catch (error: any) {
            showError(error?.message || 'بارگذاری تصویر پروفایل انجام نشد.');
        } finally {
            setIsLoading(false);
            resetNativeInput();
        }
    };

    const handleRemove = async () => {
        if (!profile?.avatar || isLoading) return;

        setIsLoading(true);

        try {
            const response = await fetch(PROFILE_AVATAR_URL, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(String(await getResponseErrorMessage(response)));
            }

            update({ ...profile, avatar: '' });
            immediateToast('success', {
                message: 'تصویر پروفایل حذف شد',
                timeout: 2000,
            });
        } catch (error: any) {
            showError(error?.message || 'حذف تصویر پروفایل انجام نشد.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                minWidth: 0,
            }}
        >
            <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                hidden
                onChange={handleFileChange}
                disabled={isLoading}
            />

            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                    src={profile?.avatar || undefined}
                    onClick={openFilePicker}
                    sx={{
                        width: 76,
                        height: 76,
                        bgcolor: profile?.avatar_color || 'primary.main',
                        color: 'primary.contrastText',
                        cursor: isLoading ? 'default' : 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 1,
                        fontSize: 24,
                        opacity: isLoading ? 0.65 : 1,
                    }}
                >
                    {profile?.name?.substring(0, 1)}
                </Avatar>

                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <IconButton
                        onClick={profile?.avatar ? handleRemove : openFilePicker}
                        size="small"
                        color={profile?.avatar ? 'error' : 'default'}
                        aria-label={
                            profile?.avatar
                                ? 'حذف تصویر پروفایل'
                                : 'انتخاب تصویر پروفایل'
                        }
                        sx={{
                            position: 'absolute',
                            insetInlineEnd: -6,
                            bottom: -4,
                            width: 30,
                            height: 30,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: profile?.avatar
                                ? 'error.main'
                                : 'divider',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'background.paper' },
                        }}
                    >
                        <i
                            className={
                                profile?.avatar
                                    ? 'fa-light fa-trash-can'
                                    : 'fa-light fa-camera'
                            }
                            style={{ fontSize: 12 }}
                        />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    className="font-yekan-heavy"
                    sx={{ color: 'text.primary', fontSize: { xs: 15, md: 16 } }}
                >
                    تصویر پروفایل
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ mt: 0.35, color: 'text.secondary', lineHeight: 1.9 }}
                >
                    تصویر JPG، JPEG یا PNG تا حداکثر ۲ مگابایت انتخاب کنید.
                </Typography>
            </Box>
        </Box>
    );
};

export default ProfileAvatarUploader;
